import React from 'react';
import Cookies from 'js-cookie';
import * as serverRenderingUtils from 'helpers/serverRenderingUtils';

type LocaleProviderValue = {
  changeLocale: (newLocale: string) => void;
  enableI18N: boolean;
  locale: string;
  showPseudoLang: boolean;
};

export const LocaleContext = React.createContext<LocaleProviderValue>({
  changeLocale: () => {},
  enableI18N: false,
  locale: 'en',
  showPseudoLang: false,
});

const shouldEnableI18N = (hostname: string) =>
  /localhost/.test(hostname) ||
  /development.anchor.fm/.test(hostname) ||
  /website-(.+).anchor.fm/.test(hostname);

export function LocaleProvider({
  locale = 'en',
  children,
}: {
  locale?: string;
  children: React.ReactNode;
}): React.ReactElement {
  // enableI18N Flag for 2022 i18n Launch (local, dev, and ad hoc environments only)
  // TODO: https://anchorfm.atlassian.net/browse/CAST-2659 - Set enableI18N flag to true for Q1 Launch, then remove when ready
  const enableI18N =
    !serverRenderingUtils.windowUndefined() &&
    shouldEnableI18N(window.location.hostname);
  const showPseudoLang =
    !serverRenderingUtils.windowUndefined() &&
    shouldEnableI18N(window.location.hostname);
  const [currentLocale, setCurrentLocale] = React.useState<string>(locale);

  const persistLocale = (requestedLocale: string) => {
    Cookies.set('i18n', requestedLocale);
  };

  const changeLocale = (requestedLocale: string) => {
    // Safety check to not run the changeLanguage function when unneccessary
    if (requestedLocale === currentLocale) {
      /* eslint-disable-next-line no-console */
      console.log('The preferred language is already set: ', currentLocale);
      return;
    }
    // Keeps HTML document lang in sync with selected locale for screen readers to use the appropriate pronunciation
    document.documentElement.setAttribute('lang', requestedLocale);
    setCurrentLocale(requestedLocale);
    if (enableI18N) persistLocale(requestedLocale);
  };

  return (
    <LocaleContext.Provider
      value={{
        locale: currentLocale,
        enableI18N,
        changeLocale,
        showPseudoLang,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}
